import {
  Phone,
  PhoneCall,
  PhoneIncoming,
  PhoneOff,
  Voicemail,
  MessageSquare,
  UserCircle2,
  Play,
  ChevronDown,
  Quote,
  ArrowUpRight,
  ShieldAlert,
  Activity,
  Radio,
  Clock,
  Sparkles,
} from "lucide-react";
import { useState } from "react";

/* --- Data --------------------------------------------------------- */

const STATUS_METRICS = [
  { id: "sb-calls", label: "Calls Today", value: "23", accent: "#00C2A8", Icon: PhoneCall },
  { id: "sb-vm", label: "Voicemails Left", value: "8", accent: "#F59E0B", Icon: Voicemail },
  { id: "sb-live", label: "Live Connections", value: "2", accent: "#0EA5E9", Icon: Radio, pulse: true },
  { id: "sb-demos", label: "Demos Booked from Voice", value: "6", accent: "#00C2A8", Icon: ArrowUpRight },
];

const ACTIVE_CALLS = [
  {
    id: "live-1",
    name: "Dr. Priya Nair",
    company: "Pacific Surgical",
    duration: "1:42",
    phase: "Qualification in progress",
    script: "Standard Warm Lead",
    stage: "Handling objection — “Not the right time”",
    stageTone: "amber",
  },
  {
    id: "live-2",
    name: "Kevin Marsh",
    company: "Heartland Hospital",
    duration: "0:23",
    phase: "Opening pitch",
    script: "Fast-Track Day 1",
    stage: "Introduction · warming up",
    stageTone: "teal",
  },
];

const RECENT_CALLS = [
  {
    id: "rc-1",
    name: "Dr. Sarah Chen",
    icon: "connected",
    outcome: "Connected · Qualified → booked demo",
    duration: "3:14",
    status: { tone: "green", label: "Success" },
  },
  {
    id: "rc-2",
    name: "James Park",
    icon: "connected",
    outcome: "Connected · Already booked — confirmed",
    duration: "0:45",
    status: { tone: "green", label: "Success" },
  },
  {
    id: "rc-3",
    name: "Amanda Torres",
    icon: "voicemail",
    outcome: "No answer · Voicemail left + SMS queued",
    duration: "—",
    status: { tone: "blue", label: "Processing", pulse: true },
  },
  {
    id: "rc-4",
    name: "Dr. Marcus Webb",
    icon: "connected",
    outcome: "Connected · “Send me info” → email triggered",
    duration: "2:01",
    status: { tone: "teal", label: "Active" },
  },
  {
    id: "rc-5",
    name: "Robert Kim",
    icon: "missed",
    outcome: "No answer · Voicemail full → email fallback",
    duration: "—",
    status: { tone: "teal", label: "Active" },
  },
  {
    id: "rc-6",
    name: "Dr. Lisa Tran",
    icon: "inbound",
    outcome: "Inbound callback · Qualified → transferred to rep",
    duration: "4:22",
    status: { tone: "green", label: "Success" },
  },
];

const OUTCOMES = [
  { id: "oc-connected", label: "Connected & qualified", pct: 34, color: "#00C2A8" },
  { id: "oc-vm", label: "Voicemail left", pct: 35, color: "#0EA5E9" },
  { id: "oc-noanswer", label: "No answer", pct: 22, color: "#F59E0B" },
  { id: "oc-inbound", label: "Inbound callbacks", pct: 9, color: "#7A9BB5" },
];

const ESCALATION_RULES = [
  "Prospect asks a clinical-safety question",
  "Prospect is ready to buy now and asks for pricing",
  "3+ consecutive unanswered attempts on a hot lead",
  "Inbound caller identifies as a GPO procurement contact",
];

/* --- Helpers ------------------------------------------------------ */

function Pill({ tone, label, pulse }) {
  const cls = {
    teal: "pill pill-teal",
    blue: "pill pill-blue",
    amber: "pill pill-amber",
    green: "pill pill-green",
    red: "pill pill-red",
    muted: "pill pill-muted",
  }[tone];
  return (
    <span className={cls}>
      <span className={`pill-dot ${pulse ? "pill-dot-pulse" : ""}`} />
      {label}
    </span>
  );
}

function callIcon(kind) {
  if (kind === "connected") return { Icon: PhoneCall, color: "#22C55E" };
  if (kind === "voicemail") return { Icon: Voicemail, color: "#F59E0B" };
  if (kind === "missed") return { Icon: PhoneOff, color: "#EF4444" };
  if (kind === "inbound") return { Icon: PhoneIncoming, color: "#0EA5E9" };
  return { Icon: Phone, color: "#7A9BB5" };
}

/* --- Sub-components ----------------------------------------------- */

function StatusBar() {
  return (
    <section
      data-testid="voice-status-bar"
      className="card-glass p-5 enter-up"
      style={{
        background:
          "linear-gradient(135deg, rgba(0, 194, 168, 0.06) 0%, rgba(14, 165, 233, 0.03) 100%), var(--bg-elevated)",
      }}
    >
      <div className="flex flex-wrap items-center gap-6">
        {/* Agent identity */}
        <div className="flex items-center gap-3 flex-1 min-w-[260px]">
          <div
            className="flex items-center justify-center rounded-lg relative"
            style={{
              width: 44,
              height: 44,
              background: "linear-gradient(135deg, #00C2A8, #0EA5E9)",
              boxShadow: "0 0 24px rgba(0, 194, 168, 0.35)",
            }}
          >
            <PhoneCall size={20} color="#0F1923" strokeWidth={2.5} />
            <span
              style={{
                position: "absolute",
                bottom: -2,
                right: -2,
                width: 12,
                height: 12,
                borderRadius: 999,
                background: "#22C55E",
                border: "2px solid var(--bg-elevated)",
                boxShadow: "0 0 8px rgba(34, 197, 94, 0.7)",
              }}
            />
          </div>
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                letterSpacing: "0.12em",
                color: "var(--text-muted)",
                textTransform: "uppercase",
              }}
            >
              Voice Agent · Vapi
            </div>
            <div
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 700,
                fontSize: 15,
                letterSpacing: "-0.01em",
                color: "var(--text-primary)",
              }}
              data-testid="voice-agent-name"
            >
              Alex — MedDevice Nurture Voice Agent
            </div>
            <div className="mt-1.5">
              <Pill tone="green" label="Active · Handling calls" pulse />
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="hidden lg:block" style={{ width: 1, alignSelf: "stretch", background: "var(--border-divider)" }} />

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-5 flex-1">
          {STATUS_METRICS.map((m) => {
            const { Icon } = m;
            return (
              <div key={m.id} data-testid={m.id}>
                <div className="flex items-center gap-1.5 mb-1">
                  <Icon size={12} color={m.accent} />
                  <span
                    style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: 10,
                      letterSpacing: "0.10em",
                      color: "var(--text-muted)",
                      textTransform: "uppercase",
                    }}
                  >
                    {m.label}
                  </span>
                  {m.pulse && (
                    <span
                      style={{
                        width: 6, height: 6, borderRadius: 999,
                        background: m.accent, boxShadow: `0 0 8px ${m.accent}`,
                      }}
                    />
                  )}
                </div>
                <div
                  style={{
                    fontFamily: "'JetBrains Mono', monospace",
                    fontWeight: 600,
                    fontSize: 24,
                    color: "var(--text-primary)",
                    letterSpacing: "-0.02em",
                    lineHeight: 1.1,
                  }}
                >
                  {m.value}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ActiveCallCard({ call, i }) {
  return (
    <article
      data-testid={call.id}
      className="card-glass p-5 enter-up"
      style={{
        animationDelay: `${i * 80}ms`,
        borderLeft: "3px solid #F59E0B",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Live pill */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div
            className="flex items-center justify-center rounded-md relative"
            style={{
              width: 36, height: 36,
              background: "rgba(245, 158, 11, 0.10)",
              border: "1px solid rgba(245, 158, 11, 0.30)",
            }}
          >
            <PhoneCall size={16} color="#F59E0B" />
            <span
              style={{
                position: "absolute",
                inset: -3,
                borderRadius: 8,
                border: "1.5px solid rgba(245, 158, 11, 0.35)",
                animation: "pulse-dot 1.6s ease-out infinite",
              }}
            />
          </div>
          <div className="min-w-0">
            <div style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              {call.name}
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-secondary)" }}>
              {call.company}
            </div>
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5">
          <Pill tone="amber" label="Live" pulse />
          <div
            className="flex items-center gap-1"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 12,
              color: "#F59E0B",
              fontWeight: 600,
            }}
          >
            <Clock size={11} />
            {call.duration}
          </div>
        </div>
      </div>

      {/* Live waveform mock */}
      <div className="flex items-center gap-[3px] mb-3" style={{ height: 22 }}>
        {[...Array(28)].map((_, wi) => {
          const heights = [10, 16, 22, 14, 8, 18, 12, 20, 16, 10, 22, 14, 8, 6, 18, 22, 16, 10, 20, 14, 8, 22, 16, 12, 18, 8, 14, 20];
          return (
            <span
              key={wi}
              style={{
                width: 2.5,
                height: heights[wi],
                background: "linear-gradient(180deg, #F59E0B, #00C2A8)",
                borderRadius: 999,
                opacity: 0.7,
                animation: `wave-bar 1.2s ease-in-out ${wi * 0.05}s infinite alternate`,
              }}
            />
          );
        })}
      </div>

      {/* Meta grid */}
      <div className="grid grid-cols-3 gap-3 mb-3">
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 2 }}>
            Phase
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
            {call.phase}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 2 }}>
            Vapi Script
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
            {call.script}
          </div>
        </div>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 2 }}>
            Stage
          </div>
          <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: `${call.stageTone === "amber" ? "#F59E0B" : "#00C2A8"}`, fontWeight: 500 }}>
            {call.stage}
          </div>
        </div>
      </div>

      {/* CTA */}
      <button
        data-testid={`${call.id}-transcript-btn`}
        className="w-full flex items-center justify-center gap-2 rounded-lg"
        style={{
          padding: "10px 14px",
          background: "rgba(0, 194, 168, 0.08)",
          border: "1px solid rgba(0, 194, 168, 0.30)",
          color: "#00C2A8",
          fontFamily: "Inter, sans-serif",
          fontSize: 12.5,
          fontWeight: 600,
          cursor: "pointer",
          transition: "all 150ms ease",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(0, 194, 168, 0.14)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(0, 194, 168, 0.08)")}
      >
        <Play size={12} />
        View Transcript Live
      </button>
    </article>
  );
}

function ActiveCalls() {
  return (
    <article data-testid="active-calls" className="card-glass p-6">
      <header className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.30)" }}
          >
            <Radio size={14} color="#F59E0B" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Active Calls
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              {ACTIVE_CALLS.length} in progress · streaming
            </div>
          </div>
        </div>
        <Pill tone="amber" label="Live" pulse />
      </header>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        {ACTIVE_CALLS.map((c, i) => (
          <ActiveCallCard key={c.id} call={c} i={i} />
        ))}
      </div>
    </article>
  );
}

function RecentCalls() {
  return (
    <article data-testid="recent-calls" className="card-glass p-6">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.25)" }}
          >
            <Activity size={14} color="#00C2A8" />
          </div>
          <div>
            <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 15, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Recent Calls
            </h2>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Last 6 · voice agent · today
            </div>
          </div>
        </div>
      </header>

      <div>
        {RECENT_CALLS.map((c, i) => {
          const { Icon, color } = callIcon(c.icon);
          return (
            <div
              key={c.id}
              data-testid={c.id}
              className="feed-row enter-up"
              style={{
                animationDelay: `${i * 40}ms`,
                gridTemplateColumns: "28px 1fr auto auto",
                gap: 12,
              }}
            >
              <div
                className="flex items-center justify-center rounded-md"
                style={{ width: 28, height: 28, background: `${color}12`, border: `1px solid ${color}30` }}
              >
                <Icon size={13} color={color} />
              </div>
              <div className="min-w-0">
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 13, fontWeight: 500, color: "var(--text-primary)", letterSpacing: "-0.005em" }}>
                  {c.name}
                </div>
                <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-secondary)", lineHeight: 1.4, marginTop: 1 }}>
                  {c.outcome}
                </div>
              </div>
              <div
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  color: c.duration === "—" ? "var(--text-muted)" : "var(--text-secondary)",
                  fontWeight: 500,
                  minWidth: 40,
                  textAlign: "right",
                }}
              >
                {c.duration}
              </div>
              <Pill {...c.status} />
            </div>
          );
        })}
      </div>
    </article>
  );
}

function DonutChart() {
  const size = 180;
  const stroke = 22;
  const R = (size - stroke) / 2;
  const C = 2 * Math.PI * R;
  const total = OUTCOMES.reduce((a, b) => a + b.pct, 0);

  let offset = 0;
  const arcs = OUTCOMES.map((o) => {
    const len = (o.pct / total) * C;
    const arc = {
      ...o,
      strokeDasharray: `${len} ${C - len}`,
      strokeDashoffset: -offset,
    };
    offset += len;
    return arc;
  });

  const largest = OUTCOMES.reduce((a, b) => (a.pct >= b.pct ? a : b));

  return (
    <div className="flex items-center gap-6">
      <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
        <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
          <circle
            cx={size / 2}
            cy={size / 2}
            r={R}
            fill="none"
            stroke="rgba(30, 48, 72, 0.5)"
            strokeWidth={stroke}
          />
          {arcs.map((a, i) => (
            <circle
              key={a.id}
              cx={size / 2}
              cy={size / 2}
              r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={stroke}
              strokeDasharray={a.strokeDasharray}
              strokeDashoffset={a.strokeDashoffset}
              strokeLinecap="butt"
              style={{
                transition: "stroke-dashoffset 600ms ease",
                filter: `drop-shadow(0 0 6px ${a.color}44)`,
              }}
              data-testid={`arc-${a.id}`}
            />
          ))}
        </svg>
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 9.5,
              letterSpacing: "0.14em",
              color: "var(--text-muted)",
              textTransform: "uppercase",
            }}
          >
            Largest
          </div>
          <div
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontWeight: 600,
              fontSize: 26,
              color: largest.color,
              letterSpacing: "-0.02em",
              marginTop: 2,
            }}
          >
            {largest.pct}%
          </div>
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 10.5,
              color: "var(--text-secondary)",
              maxWidth: 90,
              textAlign: "center",
              lineHeight: 1.2,
              marginTop: 2,
            }}
          >
            {largest.label}
          </div>
        </div>
      </div>

      <ul className="flex-1 space-y-2.5 min-w-0">
        {OUTCOMES.map((o) => (
          <li key={o.id} data-testid={`legend-${o.id}`} className="flex items-center gap-2.5">
            <span
              style={{
                width: 10,
                height: 10,
                borderRadius: 3,
                background: o.color,
                flexShrink: 0,
                boxShadow: `0 0 8px ${o.color}55`,
              }}
            />
            <div className="flex-1 min-w-0">
              <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-primary)", fontWeight: 500 }}>
                {o.label}
              </div>
              <div className="mt-1 progress-track">
                <div className="progress-fill" style={{ width: `${o.pct}%`, background: o.color }} />
              </div>
            </div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 600,
                fontSize: 13,
                color: o.color,
                minWidth: 40,
                textAlign: "right",
              }}
            >
              {o.pct}%
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

function OutcomeBreakdown() {
  return (
    <article data-testid="outcome-breakdown" className="card-glass p-5">
      <header className="flex items-center gap-2.5 mb-5">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.25)" }}
        >
          <Activity size={14} color="#0EA5E9" />
        </div>
        <div>
          <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Outcome Breakdown
          </h3>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            Last 24h · outbound + inbound
          </div>
        </div>
      </header>
      <DonutChart />
    </article>
  );
}

function VoicemailTranscript() {
  const [open, setOpen] = useState(true);
  return (
    <article data-testid="voicemail-transcript" className="card-glass p-5">
      <header className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2.5">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 28, height: 28, background: "rgba(245, 158, 11, 0.10)", border: "1px solid rgba(245, 158, 11, 0.30)" }}
          >
            <Voicemail size={14} color="#F59E0B" />
          </div>
          <div>
            <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
              Last Voicemail Transcript
            </h3>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Voice-cloned · AI-generated · 0:24
            </div>
          </div>
        </div>
        <button
          data-testid="transcript-toggle"
          onClick={() => setOpen((v) => !v)}
          className="flex items-center justify-center rounded-md"
          style={{
            width: 28, height: 28,
            background: "var(--bg-elevated)",
            border: "1px solid var(--border-divider)",
            color: "var(--text-secondary)",
            cursor: "pointer",
            transition: "transform 200ms ease",
          }}
        >
          <ChevronDown
            size={14}
            style={{ transform: open ? "rotate(180deg)" : "rotate(0)", transition: "transform 200ms ease" }}
          />
        </button>
      </header>

      {/* Recipient */}
      <div className="flex items-center gap-4 mb-3 pb-3" style={{ borderBottom: "1px solid rgba(30, 48, 72, 0.6)" }}>
        <div className="flex items-center gap-2">
          <UserCircle2 size={14} color="var(--text-muted)" />
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Recipient
            </div>
            <div style={{ fontFamily: "Inter, sans-serif", fontSize: 12, fontWeight: 500, color: "var(--text-primary)" }}>
              Amanda Torres
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 ml-auto">
          <Clock size={14} color="var(--text-muted)" />
          <div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 9.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
              Time
            </div>
            <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: "var(--text-primary)" }}>
              10:34 am
            </div>
          </div>
        </div>
      </div>

      {open && (
        <div
          className="enter-up"
          style={{
            padding: 14,
            borderRadius: 8,
            background: "rgba(15, 25, 35, 0.6)",
            border: "1px dashed rgba(245, 158, 11, 0.30)",
          }}
        >
          {/* Mini waveform */}
          <div className="flex items-center gap-3 mb-3">
            <button
              className="flex items-center justify-center rounded-full flex-shrink-0"
              style={{
                width: 28, height: 28,
                background: "#F59E0B",
                color: "#0F1923",
                cursor: "pointer",
                border: "none",
              }}
              data-testid="vm-play"
            >
              <Play size={12} fill="#0F1923" />
            </button>
            <div className="flex items-end gap-[2px] flex-1" style={{ height: 22 }}>
              {[8, 12, 16, 20, 14, 10, 18, 22, 16, 12, 20, 14, 8, 18, 22, 16, 10, 14, 20, 18, 12, 8, 16, 20, 14, 22, 18, 12, 8, 14, 20, 16, 10, 18, 14, 8, 20, 22, 16, 12].map((h, i) => (
                <span
                  key={i}
                  style={{
                    width: 2,
                    height: h,
                    background: i < 8 ? "#F59E0B" : "rgba(245, 158, 11, 0.3)",
                    borderRadius: 999,
                  }}
                />
              ))}
            </div>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10.5,
                color: "var(--text-muted)",
                whiteSpace: "nowrap",
              }}
            >
              0:04 / 0:24
            </span>
          </div>

          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 13,
              lineHeight: 1.65,
              color: "var(--text-primary)",
              fontStyle: "italic",
            }}
          >
            <span style={{ color: "#F59E0B", fontStyle: "normal", fontFamily: "'JetBrains Mono', monospace", fontSize: 10, marginRight: 6 }}>
              [00:00]
            </span>
            "Hi Amanda, this is Alex calling from [Company]. I'm following up after MedTech Summit last week — we spoke briefly about device setup times. I'd love to schedule 15 minutes to show you what we built. I'll send you a text in a few minutes with a booking link. Talk soon."
          </div>

          <div
            className="mt-3 flex items-center gap-2"
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              background: "rgba(14, 165, 233, 0.08)",
              border: "1px solid rgba(14, 165, 233, 0.25)",
            }}
          >
            <MessageSquare size={12} color="#0EA5E9" />
            <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, color: "#0EA5E9", letterSpacing: "0.04em" }}>
              SMS with booking link auto-queued · 98% open rate channel
            </span>
          </div>
        </div>
      )}
    </article>
  );
}

function EscalationRules() {
  return (
    <article data-testid="escalation-rules" className="card-glass p-5">
      <header className="flex items-center gap-2.5 mb-4">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 28, height: 28, background: "rgba(239, 68, 68, 0.10)", border: "1px solid rgba(239, 68, 68, 0.30)" }}
        >
          <ShieldAlert size={14} color="#EF4444" />
        </div>
        <div>
          <h3 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 14, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
            Escalation Rules
          </h3>
          <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
            When voice agent hands off to a live rep
          </div>
        </div>
      </header>

      <ul className="space-y-2.5">
        {ESCALATION_RULES.map((rule, i) => (
          <li
            key={i}
            data-testid={`rule-${i}`}
            className="flex items-start gap-3 enter-up"
            style={{
              animationDelay: `${i * 60}ms`,
              padding: 10,
              borderRadius: 8,
              background: "rgba(15, 25, 35, 0.5)",
              border: "1px solid rgba(30, 48, 72, 0.5)",
            }}
          >
            <span
              style={{
                width: 22,
                height: 22,
                borderRadius: 999,
                background: "rgba(239, 68, 68, 0.10)",
                border: "1px solid rgba(239, 68, 68, 0.30)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 10,
                color: "#EF4444",
                fontWeight: 600,
              }}
            >
              {i + 1}
            </span>
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12.5, lineHeight: 1.5, color: "var(--text-primary)" }}>
              {rule}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function BlueprintCallout() {
  return (
    <section
      data-testid="blueprint-callout"
      className="card-glass p-6 relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, rgba(14, 165, 233, 0.06) 0%, rgba(0, 194, 168, 0.04) 100%), var(--bg-elevated)",
      }}
    >
      <div
        style={{
          position: "absolute",
          top: -30,
          right: -20,
          color: "rgba(14, 165, 233, 0.08)",
        }}
      >
        <Quote size={160} strokeWidth={1} />
      </div>

      <div className="relative">
        <div className="flex items-center gap-2 mb-4">
          <div
            className="flex items-center justify-center rounded-md"
            style={{ width: 26, height: 26, background: "rgba(14, 165, 233, 0.10)", border: "1px solid rgba(14, 165, 233, 0.30)" }}
          >
            <Sparkles size={12} color="#0EA5E9" />
          </div>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              letterSpacing: "0.14em",
              color: "#0EA5E9",
              textTransform: "uppercase",
            }}
          >
            Blueprint Connection · SMS Leverage
          </span>
        </div>

        <blockquote
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 17.5,
            fontWeight: 500,
            lineHeight: 1.55,
            letterSpacing: "-0.01em",
            color: "var(--text-primary)",
            maxWidth: 940,
            margin: 0,
          }}
        >
          "Text messaging achieves a{" "}
          <span style={{ color: "#0EA5E9" }}>98% open rate</span> — making it the highest-leverage channel for demo-booking follow-up. Every voicemail the voice agent leaves is immediately followed by an{" "}
          <span style={{ color: "#00C2A8" }}>SMS with a direct booking link</span>."
        </blockquote>

        <div
          className="mt-5 flex items-center gap-2"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: "var(--text-secondary)",
            letterSpacing: "0.06em",
          }}
        >
          <ArrowUpRight size={12} color="#0EA5E9" />
          Blueprint Insight 4
        </div>
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function VoiceAgent() {
  return (
    <div data-testid="voice-agent-page" className="max-w-[1400px] mx-auto space-y-8">
      {/* Header */}
      <section data-testid="voice-header" className="enter-up">
        <div className="flex items-start gap-4">
          <div
            style={{
              width: 4,
              alignSelf: "stretch",
              background: "linear-gradient(180deg, #00C2A8, #0EA5E9)",
              borderRadius: 4,
              minHeight: 68,
            }}
          />
          <div>
            <div
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                letterSpacing: "0.14em",
                color: "var(--accent-teal)",
                textTransform: "uppercase",
                marginBottom: 6,
              }}
            >
              Module · Tier 2 Growth Engine
            </div>
            <h1
              style={{
                fontFamily: "Inter, sans-serif",
                fontWeight: 800,
                fontSize: 28,
                letterSpacing: "-0.02em",
                color: "var(--text-primary)",
                lineHeight: 1.2,
                marginBottom: 8,
              }}
            >
              Voice Follow-Up Agent
            </h1>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.55,
                color: "var(--text-secondary)",
                maxWidth: 820,
              }}
            >
              Always-on Vapi voice agent handling outbound MQL follow-up and inbound qualification — 24/7, without rep time.
            </p>
          </div>
        </div>
      </section>

      {/* Status Bar */}
      <StatusBar />

      {/* Main 2-column */}
      <section className="grid grid-cols-12 gap-6">
        <div className="col-span-12 lg:col-span-7 space-y-6">
          <ActiveCalls />
          <RecentCalls />
        </div>
        <div className="col-span-12 lg:col-span-5 space-y-6">
          <OutcomeBreakdown />
          <VoicemailTranscript />
          <EscalationRules />
        </div>
      </section>

      {/* Bottom blueprint callout */}
      <BlueprintCallout />
    </div>
  );
}
